import { ButtonBase } from "@components/Button";
import InviteModal from "@components/InviteModal";
import JoinLeagueModal from "@components/JoinLeagueModal";
import LeaveLeagueModal from "@components/LeaveLeagueModal";
import EmailIcon from "@components/svgs/email-icon.svg";
import FacebookIcon from "@components/svgs/facebook-icon.svg";
import TwitterIcon from "@components/svgs/twitter-icon.svg";
import { MyEntryRow, PaginationArea, Table, Td, THead } from "@components/Table";
import useAnalytics from "@hooks/useAnalytics";
import useClientContext from "@hooks/useClientContext";
import useGame from "@hooks/useGame";
import useLeague, { LeagueMember } from "@hooks/useLeague";
import useMyLeagues from "@hooks/useMyLeagues";
import Link from "next/dist/client/link";
import { useEffect, useMemo, useState } from "react";
import ReactPaginate from "react-paginate";
import { Column, useTable } from "react-table";
import { media, publicBasePath, pxToRem } from "src/styles/styleUtils";
import styled from "styled-components";
import { setModal } from "../../../hooks/useClientContext";
import settings from "../../../settings";
const { API_BACKEND_HOSTNAME } = settings;

const LeaguePageContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;

  .loading {
    padding-top: ${pxToRem(200)};
    height: ${pxToRem(22)};
    color: white;
    font-size: ${pxToRem(28)};
    font-family: ${({ theme }) => theme.fontSecondary};
    letter-spacing: 0px;
    line-height: ${pxToRem(22)};
  }
`;

const LeagueContainer = styled.div`
  width: 100%;
  max-width: ${pxToRem(740)};
  padding: ${pxToRem(48)} 0;

  ${media.tablet} {
    padding: ${pxToRem(48)} ${pxToRem(24)};
  }
`;

const Header = styled.div`
  min-height: ${pxToRem(94)};
  display: flex;
  justify-content: space-between;
  border-bottom: ${pxToRem(2)} solid white;
  margin-bottom: ${pxToRem(15)};
  flex-direction: row;
  h2 {
    color: white;
    font-size: ${pxToRem(28)};
    font-family: ${({ theme }) => theme.fontPrimary};
    line-height: ${pxToRem(22)};
    font-weight: bold;
    text-transform: uppercase;
  }

  h4 {
    color: white;
    font-size: ${pxToRem(10)};
    text-transform: uppercase;
  }
`;

const SocialShare = styled.div`
  display: flex;
  padding-top: ${pxToRem(25)};
  min-width: ${pxToRem(112)};

  div {
    flex: 0 0 ${pxToRem(32)};
    min-height: ${pxToRem(30)};
    display: flex;
    justify-content: center;
    align-items: center;
    border: ${pxToRem(2)} solid white;

    &:not(:last-child) {
      margin-right: ${pxToRem(8)};
    }
  }

  a {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const LeagueActionButton = styled.button`
  ${ButtonBase}
  width: 100%;
  margin-top: ${pxToRem(10)};
  color: ${({ theme }) => theme.grey_150};
  font-size: ${pxToRem(10)};
  font-weight: bold;
  text-align: center;
  text-decoration: underline;
  text-transform: none;
`;

const LeagueHeader = styled.div`
  color: white;
  font-size: ${pxToRem(22)};
  font-family: ${({ theme }) => theme.fontSecondary};
  line-height: ${pxToRem(22)};
  margin-bottom: ${pxToRem(15)};
`;

const LeaguePage = ({ leagueId }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const { data } = useLeague(currentPage);
  const { data: myLeagues } = useMyLeagues();
  const { data: game } = useGame();
  const [{ currentEntry }, dispatch] = useClientContext();

  const { memberships, league, pagination } = data;
  const { pages: numberOfPages } = pagination;
  const isMember = myLeagues?.leagues?.some((l) => l.id === league.id) ?? false;
  const shouldShowEntryRow =
    currentEntry?.id && isMember && memberships?.every((m) => m.id !== currentEntry.id);
  const myRank = currentEntry?.entry_bar?.league_ranks?.[league.id] || "-";

  const pagesArr = [];
  for (let i = 1; i < numberOfPages; i++) {
    pagesArr.push(i);
  }

  const round = game.rounds.find((r) => r.round_id === game.state?.current_round);
  const now = Date.now();
  const isPostLock = now > round?.locks_at ?? false;

  useEffect(() => {
    window.scrollTo({ behavior: "smooth", top: 0 });
  }, [currentPage]);

  const tableData = useMemo(() => memberships, [memberships]);
  const columns = useMemo<Column<LeagueMember>[]>(
    () => [
      {
        Header: "Rank",
        accessor: (member) => member.rank,
        style: {
          width: "72px",
        },
      },
      {
        Header: "",
        accessor: "avatar_id",
        style: {
          verticalAlign: "bottom",
          padding: "0px",
          width: "32px",
        },
      },
      {
        Header: "Entry Name",
        accessor: (member) => {
          return isPostLock ? (
            <>
              <Link href={`/leagues/${leagueId}/user/${member.id}`}>{member.name}</Link>
            </>
          ) : (
            member.name
          );
        },
        className: "main",
      },
      {
        Header: "Pts",
        accessor: (member) => member.points,
      },
    ],
    [isPostLock, leagueId],
  );
  const tableInstance = useTable<LeagueMember>({ columns, data: tableData });
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;

  const { trackPage, trackEvent } = useAnalytics();
  useEffect(() => {
    trackPage("leagues:show");
  }, [trackPage]);

  return (
    <LeaguePageContainer>
      <LeagueContainer>
        <Header>
          <div>
            <h2>{league.name}</h2>
            <h4>Members {league.memberships_count}</h4>
          </div>

          <div>
            <SocialShare>
              <div className="fb">
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${API_BACKEND_HOSTNAME}${publicBasePath}`}
                  rel="noreferrer noopener"
                  target="_blank"
                  onClick={() => trackEvent("league_invite:facebook")}
                >
                  <FacebookIcon />
                </a>
              </div>
              <div className="twitter">
                <a
                  href={`https://twitter.com/intent/tweet?url=https%3A%2F%2Fnas.cr%2Fplayoffsgrid&text=I%27m%20playing%20The%20%23NASCAR%20Playoffs%20Grid%20Challenge.%20Join%20my%20league%20%22${encodeURIComponent(
                    league.name,
                  )}%22`}
                  rel="noreferrer noopener"
                  target="_blank"
                  onClick={() => trackEvent("league_invite:twitter")}
                >
                  <TwitterIcon />
                </a>
              </div>
              <div className="email">
                <button
                  onClick={() => dispatch(setModal("INVITE_MODAL"))}
                  css={ButtonBase}
                  disabled={!isMember}
                >
                  <EmailIcon />
                </button>
                <InviteModal leagueId={league.id} />
              </div>
            </SocialShare>
            <LeagueActionButton
              onClick={() =>
                dispatch(setModal(isMember ? "LEAVE_LEAGUE" : "JOIN_LEAGUE", { league }))
              }
            >
              {isMember ? "Remove from League" : "Join League"}
            </LeagueActionButton>
          </div>
        </Header>

        <LeagueHeader>Overall League Standings</LeagueHeader>
        <Table {...getTableProps()}>
          <colgroup>
            <col />
            <col />
            <col className="main" />
            <col />
          </colgroup>
          <THead>
            {
              // Loop over the header rows
              headerGroups.map((headerGroup) => (
                // Apply the header row props
                <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                  {
                    // Loop over the headers in each row
                    headerGroup.headers.map((column) => (
                      // Apply the header cell props
                      <th
                        key={column.id}
                        {...column.getHeaderProps({
                          className: column.className,
                          style: column.style,
                        })}
                      >
                        {
                          // Render the header
                          column.render("Header")
                        }
                      </th>
                    ))
                  }
                </tr>
              ))
            }
          </THead>
          {/* Apply the table body props */}
          <tbody {...getTableBodyProps()}>
            {shouldShowEntryRow && (
              <MyEntryRow>
                <Td style={columns[0].style}>{myRank}</Td>
                <Td style={columns[1].style}>
                  <img
                    src={`${publicBasePath}/images/avatar/${currentEntry.avatar_id}.png`}
                    alt="Entry Avatar"
                    draggable="false"
                    width="25"
                    height="25"
                  />
                </Td>
                <Td className="main">{currentEntry.name}</Td>
                <Td>{currentEntry.entry_bar.points.total_points}</Td>
              </MyEntryRow>
            )}
            {
              // Loop over the table rows
              rows.map((row) => {
                // Prepare the row for display
                prepareRow(row);
                return (
                  // Apply the row props
                  <tr key={row.id} {...row.getRowProps()}>
                    {
                      // Loop over the rows cells
                      row.cells.map((cell) => {
                        // Apply the cell props
                        return (
                          <Td
                            key={`${cell.row.id}-${cell.column.id}`}
                            {...cell.getCellProps({
                              className: `${cell.column.className || ""}${
                                row.original.id === currentEntry?.id ? " myEntry" : ""
                              }`,
                              style: cell.column.style,
                            })}
                          >
                            {
                              // Render the cell contents
                              cell.column.id === "avatar_id" ? (
                                <>
                                  <img
                                    src={`${publicBasePath}/images/avatar/${cell.value}.png`}
                                    alt="Entry Avatar"
                                    draggable="false"
                                    width="25"
                                    height="25"
                                  />
                                </>
                              ) : (
                                cell.render("Cell")
                              )
                            }
                          </Td>
                        );
                      })
                    }
                  </tr>
                );
              })
            }
          </tbody>
        </Table>
        <PaginationArea>
          <ReactPaginate
            pageCount={pagination.pages}
            previousLabel={
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 7.01 12.22">
                <polygon
                  fill="currentColor"
                  points="0 2.07 1.51 0 7.01 6.11 1.51 12.22 0 10.14 3.85 6.11 0 2.07"
                  fillRule="evenodd"
                />
              </svg>
            }
            nextLabel={
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 7.01 12.22">
                <polygon
                  fill="currentColor"
                  points="0 2.07 1.51 0 7.01 6.11 1.51 12.22 0 10.14 3.85 6.11 0 2.07"
                  fillRule="evenodd"
                />
              </svg>
            }
            breakLabel={"…"}
            activeClassName={"active"}
            containerClassName={"pagination"}
            pageRangeDisplayed={9}
            marginPagesDisplayed={0}
            onPageChange={({ selected }) => setCurrentPage(selected + 1)}
          />
        </PaginationArea>
      </LeagueContainer>
      <JoinLeagueModal />
      <LeaveLeagueModal />
    </LeaguePageContainer>
  );
};

export default LeaguePage;

export async function getServerSideProps(context) {
  const leagueId = context.params.id;

  return { props: { leagueId } };
}
